import React, { useState } from 'react';
import UIInput from '@/components/shared/UIInput';
import UISelect, { UISelectOption } from '@/components/shared/UISelect';
import UIButton from '@/components/shared/UIButton';
import UISectionWrapper from '@/components/shared/UISectionWrapper';
import './UISearchSection.scss';

export interface UISearchSectionProps {
  // Select конфігурація
  selectOptions?: UISelectOption[];
  selectPlaceholder?: string;
  selectValue?: string;
  selectWidth?: string;

  // Input конфігурація
  inputPlaceholder?: string;
  inputValue?: string;

  // Callbacks
  onSearch?: (searchData: SearchData) => void;
  onSelectChange?: (value: string) => void;
  onInputChange?: (value: string) => void;
  onClear?: () => void;

  // Button конфігурація
  searchButtonText?: string;

  // Поведінка
  searchOnSelectChange?: boolean; // пошук при зміні селекта
  searchOnEnter?: boolean; // пошук при натисканні Enter
  loading?: boolean; // показувати спінер під час завантаження

  // Layout
  spacing?: 'compact' | 'default' | 'tight' | 'spacious';
  className?: string;
  variant?: 'inline' | 'wrapped'; // inline - всі елементи в рядок, wrapped - з UISectionWrapper
}

export interface SearchData {
  searchTerm: string;
  filterValue: string;
}

// Мокові дані для демонстрації
const mockSelectOptions: UISelectOption[] = [
  { value: 'all', label: 'All Items' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' }
];

const UISearchSection: React.FC<UISearchSectionProps> = ({
                                                           selectOptions = mockSelectOptions,
                                                           selectPlaceholder = 'Select Option',
                                                           selectValue = '',
                                                           selectWidth = '160px',
                                                           inputPlaceholder = 'Search...',
                                                           inputValue = '',
                                                           onSearch,
                                                           onSelectChange,
                                                           onInputChange,
                                                           onClear,
                                                           searchButtonText = 'Search',
                                                           searchOnSelectChange = true,
                                                           searchOnEnter = true,
                                                           loading = false,
                                                           spacing = 'default',
                                                           className = '',
                                                           variant = 'wrapped'
                                                         }) => {
  const [internalSearchTerm, setInternalSearchTerm] = useState(inputValue);
  const [internalFilterValue, setInternalFilterValue] = useState(selectValue);
  const [demoLoading, setDemoLoading] = useState(false);

  // Демо функція з затримкою для тестування спінера
  const performSearchWithDelay = async (searchData: SearchData) => {
    setDemoLoading(true);

    // Штучна затримка 2 секунди для демонстрації спінера
    await new Promise(resolve => setTimeout(resolve, 2000));

    if (onSearch) {
      onSearch(searchData);
    } else {
      console.log('Search completed:', searchData);
    }

    setDemoLoading(false);
  };

  const isLoading = loading || demoLoading;

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setInternalFilterValue(value);

    if (onSelectChange) {
      onSelectChange(value);
    }

    // Автоматичний пошук при зміні селекта
    if (searchOnSelectChange && !isLoading) {
      const searchData: SearchData = {
        searchTerm: internalSearchTerm,
        filterValue: value
      };

      if (!onSearch) {
        performSearchWithDelay(searchData);
      } else {
        onSearch(searchData);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInternalSearchTerm(value);
    if (onInputChange) {
      onInputChange(value);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && searchOnEnter && !isLoading) {
      handleSearch();
    }
  };

  const handleSearch = () => {
    const searchData: SearchData = {
      searchTerm: internalSearchTerm,
      filterValue: internalFilterValue
    };

    // Використовуємо демо функцію для тестування або реальний колбек
    if (!onSearch) {
      performSearchWithDelay(searchData);
    } else {
      onSearch(searchData);
    }
  };

  const handleClearInput = () => {
    setInternalSearchTerm('');

    if (onInputChange) {
      onInputChange('');
    }

    // Автоматичний пошук після очистки
    const searchData: SearchData = {
      searchTerm: '',
      filterValue: internalFilterValue
    };

    if (onSearch) {
      onSearch(searchData);
    }
  };

  const baseClass = 'UISearchSection';
  const containerClass = `${baseClass} ${className}`.trim();

  const renderContent = () => (
    <div className={`${baseClass}__controls ${baseClass}__controls--${spacing}`}>
      {selectOptions && selectOptions.length > 0 && (
        <div className={`${baseClass}__select-wrapper`}>
          <UISelect
            value={internalFilterValue}
            onChange={handleSelectChange}
            options={selectOptions}
            placeholder={selectPlaceholder}
            className={`${baseClass}__select`}
            style={{ width: selectWidth }}
            disabled={isLoading}
          />
        </div>
      )}

      <div className={`${baseClass}__input-wrapper`}>
        <UIInput
          value={internalSearchTerm}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          placeholder={inputPlaceholder}
          className={`${baseClass}__input`}
          fullWidth
          showClearButton={!!internalSearchTerm && !isLoading}
          onClear={handleClearInput}
          disabled={isLoading}
        />
      </div>

      <div className={`${baseClass}__button-wrapper`}>
        <UIButton
          variant="success"
          size="medium"
          onClick={handleSearch}
          disabled={isLoading}
          className={`${baseClass}__search-button ${isLoading ? `${baseClass}__search-button--loading` : ''}`}
        >
          {isLoading ? (
            <div className={`${baseClass}__button-content`}>
              <div className={`${baseClass}__spinner`}></div>
              <span>Loading...</span>
            </div>
          ) : (
            searchButtonText
          )}
        </UIButton>
      </div>
    </div>
  );

  if (variant === 'inline') {
    return (
      <div className={containerClass}>
        {renderContent()}
      </div>
    );
  }

  return (
    <UISectionWrapper
      spacing={spacing}
      align="stretch"
      className={containerClass}
    >
      {renderContent()}
    </UISectionWrapper>
  );
};

export default UISearchSection;